import { useDynamicHeight } from '@/hooks/base/useDynamicHeight';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState } from 'react';
import TokenRow from './components/TokenRow';
import TokenHead from './components/TokenHead';
import { SortTypeEnum } from '@/request/request.enum';
import { useDailyToken } from './hooks/useDailyToken';
import { Skeleton } from '@/components/ui/skeleton';
import { ISignalDailyToken } from '@/request/signal/signal.response.interface';
import BigNumber from 'bignumber.js';
import NoData from '@/widget/NoData';
import HowWorkBtn from '@/widget/HowWorkBtn';

export interface SortConfig {
  key: 'time' | 'chg';
  direction: SortTypeEnum;
}

export default () => {
  const height = useDynamicHeight();
  const { loading, dailyList } = useDailyToken();

  const [sortConfigs, setSortConfigs] = useState<SortConfig>({
    key: 'chg',
    direction: SortTypeEnum.DESC,
  });

  const compareValues = (a: ISignalDailyToken, b: ISignalDailyToken, key: SortConfig['key']): number => {
    const getValue = (item: ISignalDailyToken): BigNumber => {
      switch (key) {
        case 'time':
          return new BigNumber(item.timestamp);
        case 'chg':
          const maxPriceChg = BigNumber(item.highestPrice).minus(item.firstAlertPrice).div(item.firstAlertPrice).toString();
          return new BigNumber(maxPriceChg);
        default:
          return new BigNumber(0);
      }
    };
    const valueA = getValue(a);
    const valueB = getValue(b);
    return sortConfigs.direction === SortTypeEnum.ASC ? valueA.minus(valueB).toNumber() : valueB.minus(valueA).toNumber();
  };

  const handleSort = (key: SortConfig['key']) => {
    if (key == 'time') return;
    setSortConfigs((prev) => ({
      key,
      direction: prev.key === key && prev.direction === SortTypeEnum.DESC ? SortTypeEnum.ASC : SortTypeEnum.DESC,
    }));
  };

  const getSortStatus = (key: SortConfig['key']): SortTypeEnum => (sortConfigs.key === key ? sortConfigs.direction : SortTypeEnum.DEFAULT);

  const sortedTokens = useMemo(() => [...dailyList].sort((a, b) => compareValues(a, b, sortConfigs.key)), [dailyList, sortConfigs]);

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: sortedTokens.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 64,
    overscan: 5,
  });

  return (
    <div>
      <div className="flex mt-[16px] mb-[12px]">
        <HowWorkBtn />
      </div>
      <div className="mt-[10px] px-[0px]">
        <TokenHead handleSort={handleSort} getSortStatus={getSortStatus} />
        {loading ? (
          <div>
            {[...Array(10)].map((_, index) => {
              return (
                <div className="h-[64px] flex items-center border-b-[1px] border-borderColor" key={index}>
                  <div className="flex-1">
                    <Skeleton className="h-[42px] w-[80%] rounded-xl" />
                  </div>
                  <div className="flex-1 flex justify-end">
                    <Skeleton className="h-[42px] w-[80%] rounded-xl" />
                  </div>
                  <div className="flex-1 flex  justify-end">
                    <Skeleton className="h-[42px] w-[80%] rounded-xl" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : sortedTokens.length > 0 ? (
          <div
            ref={parentRef}
            style={{
              height: height + 'px',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                <div
                  key={virtualRow.index}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: `64px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <TokenRow token={sortedTokens[virtualRow.index]} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <NoData />
        )}
      </div>
    </div>
  );
};
